import { useState, useEffect, useRef } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { useNavigate } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Badge } from '../components/ui/badge';
import { Checkbox } from '../components/ui/checkbox';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from '../components/ui/dialog';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { useAuth } from '../contexts/AuthContext';
import { 
  Building2, 
  Upload, 
  Camera, 
  Trash2, 
  Save, 
  RefreshCw,
  Image as ImageIcon,
  CheckCircle2,
  AlertCircle,
  X,
  Eye,
  CreditCard,
  FileText,
  MapPin,
  AlertTriangle,
  Loader2,
  Search,
  Calendar,
  Ban,
  Crown,
  Clock
} from 'lucide-react';
import { maskCNPJ, cleanCNPJ, validateCNPJ, maskCEP, cleanCEP, fetchAddressByCEP } from '../utils/brazilValidation';

// Extracted company components
import { CompanyLogoUpload } from '../components/company';

const API_URL = process.env.REACT_APP_BACKEND_URL + '/api';

const ESTADOS_BR = [
  'AC', 'AL', 'AP', 'AM', 'BA', 'CE', 'DF', 'ES', 'GO', 'MA', 'MT', 'MS', 'MG',
  'PA', 'PB', 'PR', 'PE', 'PI', 'RJ', 'RN', 'RS', 'RO', 'RR', 'SC', 'SP', 'SE', 'TO'
];

const CompanyProfile = () => {
  const { company, user, logout } = useAuth();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [companyData, setCompanyData] = useState(null);
  const [currentLogo, setCurrentLogo] = useState(null);
  const [logoLoading, setLogoLoading] = useState(false);
  const [activeTab, setActiveTab] = useState('general');
  
  // Upload states
  const [uploadModalOpen, setUploadModalOpen] = useState(false);
  const [selectedFile, setSelectedFile] = useState(null);
  const [previewUrl, setPreviewUrl] = useState(null);
  const [uploading, setUploading] = useState(false);
  
  // Financial data states
  const [financialData, setFinancialData] = useState({
    endereco_logradouro: '',
    endereco_numero: '',
    endereco_complemento: '',
    endereco_bairro: '',
    endereco_cidade: '',
    endereco_estado: '',
    endereco_cep: '',
    inscricao_estadual: '',
    inscricao_estadual_isento: false,
    inscricao_municipal: '',
    razao_social: '',
    nome_fantasia: '',
    cnpj: '',
    email_nf: ''
  });
  const [savingFinancial, setSavingFinancial] = useState(false);
  const [loadingCEP, setLoadingCEP] = useState(false);
  const [cnpjError, setCnpjError] = useState('');
  const [cepError, setCepError] = useState('');
  
  // Delete account states
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [deleteConfirmText, setDeleteConfirmText] = useState('');
  const [deleting, setDeleting] = useState(false);
  
  // Subscription states
  const [subscriptionData, setSubscriptionData] = useState(null);
  const [loadingSubscription, setLoadingSubscription] = useState(false);
  const [showCancelModal, setShowCancelModal] = useState(false);
  const [cancelling, setCancelling] = useState(false);
  
  const fileInputRef = useRef(null);

  useEffect(() => {
    fetchCompanyData();
    fetchCurrentLogo();
    fetchFinancialData();
    fetchSubscriptionData();
  }, []);

  const fetchCompanyData = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${API_URL}/company/profile`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      
      if (response.ok) {
        const data = await response.json();
        setCompanyData(data);
      } else {
        setCompanyData({
          nome: company?.nome || 'Empresa',
          cnpj: company?.cnpj || '',
          email: user?.email || ''
        });
      }
    } catch (error) {
      setCompanyData({
        nome: company?.nome || 'Empresa',
        cnpj: company?.cnpj || '',
        email: user?.email || ''
      });
    } finally {
      setLoading(false);
    }
  };

  const fetchFinancialData = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${API_URL}/company/financial-data`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (response.ok) {
        const data = await response.json();
        if (data.dados_financeiros) {
          setFinancialData(prev => ({
            ...prev,
            ...data.dados_financeiros
          }));
        }
      }
    } catch (error) {
      console.error('Error fetching financial data:', error);
    }
  };

  const fetchSubscriptionData = async () => {
    setLoadingSubscription(true);
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${API_URL}/asaas/subscription/status`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (response.ok) {
        const data = await response.json();
        setSubscriptionData(data);
      }
    } catch (error) {
      console.error('Error fetching subscription data:', error);
    } finally {
      setLoadingSubscription(false);
    }
  };

  const handleCancelSubscription = async () => {
    setCancelling(true);
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${API_URL}/asaas/subscription/cancel`, {
        method: 'POST',
        headers: { 
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });
      
      if (response.ok) {
        const data = await response.json();
        toast.success('Assinatura cancelada com sucesso');
        setShowCancelModal(false);
        fetchSubscriptionData(); // Refresh subscription data
      } else {
        const error = await response.json();
        toast.error(error.detail || 'Erro ao cancelar assinatura');
      }
    } catch (error) {
      console.error('Error cancelling subscription:', error);
      toast.error('Erro ao cancelar assinatura');
    } finally {
      setCancelling(false);
    }
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return '-';
    try {
      const date = new Date(dateStr);
      return date.toLocaleDateString('pt-BR');
    } catch {
      return dateStr;
    }
  };

  const getStatusBadge = (status) => {
    const statusMap = {
      'active': { label: 'Ativo', className: 'bg-emerald-100 text-emerald-800' },
      'trial': { label: 'Período de Teste', className: 'bg-blue-100 text-blue-800' },
      'cancelled': { label: 'Cancelado', className: 'bg-amber-100 text-amber-800' },
      'expired': { label: 'Expirado', className: 'bg-red-100 text-red-800' },
      'overdue': { label: 'Pagamento Pendente', className: 'bg-orange-100 text-orange-800' }
    };
    const s = statusMap[status] || { label: status, className: 'bg-gray-100 text-gray-800' };
    return <Badge className={s.className}>{s.label}</Badge>;
  };

  const fetchCurrentLogo = async () => {
    setLogoLoading(true);
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${API_URL}/company/logo`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      
      if (response.ok) {
        const blob = await response.blob();
        const url = URL.createObjectURL(blob);
        setCurrentLogo(url);
      } else {
        setCurrentLogo(null);
      }
    } catch (error) {
      setCurrentLogo(null);
    } finally {
      setLogoLoading(false);
    }
  };

  const handleFileSelect = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    const allowedTypes = ['image/jpeg', 'image/png', 'image/webp', 'image/gif'];
    if (!allowedTypes.includes(file.type)) {
      toast.error('Tipo de arquivo não permitido. Use JPG, PNG, WebP ou GIF.');
      return;
    }

    if (file.size > 2 * 1024 * 1024) {
      toast.error('Arquivo muito grande. Máximo: 2MB');
      return;
    }

    setSelectedFile(file);
    const url = URL.createObjectURL(file);
    setPreviewUrl(url);
    setUploadModalOpen(true);
  };

  const handleUploadLogo = async () => {
    if (!selectedFile) return;

    setUploading(true);
    const formData = new FormData();
    formData.append('file', selectedFile);

    try {
      await axios.post(`${API_URL}/company/logo`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      
      toast.success('Logo atualizado com sucesso!');
      setUploadModalOpen(false);
      
      if (previewUrl) {
        URL.revokeObjectURL(previewUrl);
      }
      setSelectedFile(null);
      setPreviewUrl(null);
      fetchCurrentLogo();
      
    } catch (error) {
      console.error('Erro no upload:', error);
      toast.error(error.response?.data?.detail || 'Erro ao enviar logo');
    } finally {
      setUploading(false);
    }
  };

  const handleCancelUpload = () => {
    if (previewUrl) {
      URL.revokeObjectURL(previewUrl);
    }
    setSelectedFile(null);
    setPreviewUrl(null);
    setUploadModalOpen(false);
    
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  const handleDragOver = (e) => {
    e.preventDefault();
    e.stopPropagation();
  };

  const handleDrop = (e) => {
    e.preventDefault();
    e.stopPropagation();
    
    const file = e.dataTransfer.files?.[0];
    if (file) {
      const syntheticEvent = {
        target: { files: [file] }
      };
      handleFileSelect(syntheticEvent);
    }
  };

  const handleCNPJChange = (value) => {
    const masked = maskCNPJ(value);
    setFinancialData(prev => ({ ...prev, cnpj: masked }));
    
    const cleaned = cleanCNPJ(value);
    if (cleaned.length === 14) {
      if (!validateCNPJ(cleaned)) {
        setCnpjError('CNPJ inválido');
      } else {
        setCnpjError('');
      }
    } else {
      setCnpjError('');
    }
  };

  const handleCEPChange = async (value) => {
    const masked = maskCEP(value);
    setFinancialData(prev => ({ ...prev, endereco_cep: masked }));
    setCepError('');
    
    const cleaned = cleanCEP(value);
    if (cleaned.length === 8) {
      setLoadingCEP(true);
      const result = await fetchAddressByCEP(cleaned);
      setLoadingCEP(false);
      
      if (result.success) {
        setFinancialData(prev => ({
          ...prev,
          endereco_logradouro: result.logradouro || prev.endereco_logradouro,
          endereco_bairro: result.bairro || prev.endereco_bairro,
          endereco_cidade: result.cidade || prev.endereco_cidade,
          endereco_estado: result.estado || prev.endereco_estado,
          endereco_complemento: result.complemento || prev.endereco_complemento
        }));
        toast.success('Endereço preenchido automaticamente!');
      } else if (result.error) {
        setCepError(result.error);
      }
    }
  };

  const handleSaveFinancialData = async () => {
    setSavingFinancial(true);
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${API_URL}/company/financial-data`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify(financialData)
      });
      
      if (response.ok) {
        toast.success('Dados financeiros salvos com sucesso!');
      } else {
        const error = await response.json();
        toast.error(error.detail || 'Erro ao salvar dados');
      }
    } catch (error) {
      console.error('Error saving financial data:', error);
      toast.error('Erro ao salvar dados financeiros');
    } finally {
      setSavingFinancial(false);
    }
  };

  const handleDeleteAccount = async () => {
    if (deleteConfirmText !== 'EXCLUIR MINHA CONTA') {
      toast.error('Digite "EXCLUIR MINHA CONTA" para confirmar');
      return;
    }
    
    setDeleting(true);
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${API_URL}/company/delete-account`, {
        method: 'DELETE',
        headers: { 'Authorization': `Bearer ${token}` }
      });
      
      if (response.ok) {
        toast.success('Conta excluída com sucesso');
        logout();
        navigate('/login');
      } else {
        const error = await response.json();
        toast.error(error.detail || 'Erro ao excluir conta');
      }
    } catch (error) {
      console.error('Error deleting account:', error);
      toast.error('Erro ao excluir conta');
    } finally {
      setDeleting(false);
    }
  };

  useEffect(() => {
    const logoUrl = currentLogo;
    const preview = previewUrl;
    return () => {
      if (logoUrl && logoUrl.startsWith('blob:')) {
        try { URL.revokeObjectURL(logoUrl); } catch(e) {}
      }
      if (preview && preview.startsWith('blob:')) {
        try { URL.revokeObjectURL(preview); } catch(e) {}
      }
    };
  }, [currentLogo, previewUrl]);

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <RefreshCw className="animate-spin text-emerald-500" size={32} />
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="company-profile-page">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-2xl font-bold text-slate-900 dark:text-white">
            Perfil da Empresa
          </h2>
          <p className="text-slate-600 dark:text-slate-400 mt-1">
            Gerencie as informações, dados financeiros e identidade visual da sua empresa
          </p>
        </div>
      </div>

      {/* Tabs */}
      <Tabs value={activeTab} onValueChange={setActiveTab} className="w-full">
        <TabsList className="grid w-full grid-cols-3 lg:w-[400px]">
          <TabsTrigger value="general" className="flex items-center gap-2">
            <Building2 size={16} />
            Geral
          </TabsTrigger>
          <TabsTrigger value="financial" className="flex items-center gap-2">
            <CreditCard size={16} />
            Financeiro
          </TabsTrigger>
          <TabsTrigger value="settings" className="flex items-center gap-2">
            <AlertTriangle size={16} />
            Configurações
          </TabsTrigger>
        </TabsList>

        {/* General Tab */}
        <TabsContent value="general" className="mt-6">
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
            {/* Logo Card */}
            <Card className="lg:col-span-1">
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <ImageIcon size={20} />
                  Logo da Empresa
                </CardTitle>
                <CardDescription>
                  O logo aparecerá em relatórios, certificados e documentos gerados pelo sistema
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="flex flex-col items-center">
                  <div 
                    className="w-40 h-40 rounded-xl border-2 border-dashed border-slate-300 dark:border-slate-600 flex items-center justify-center overflow-hidden bg-slate-50 dark:bg-slate-800 relative group"
                    data-testid="logo-display"
                    onDragOver={handleDragOver}
                    onDrop={handleDrop}
                  >
                    {logoLoading ? (
                      <RefreshCw className="animate-spin text-slate-400" size={32} />
                    ) : currentLogo ? (
                      <>
                        <img 
                          src={currentLogo} 
                          alt="Logo da empresa" 
                          className="max-w-full max-h-full object-contain"
                        />
                        <div className="absolute inset-0 bg-black/50 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center gap-2">
                          <Button
                            size="sm"
                            variant="secondary"
                            onClick={() => fileInputRef.current?.click()}
                          >
                            <Camera size={16} />
                          </Button>
                        </div>
                      </>
                    ) : (
                      <div 
                        className="flex flex-col items-center text-slate-400 cursor-pointer hover:text-slate-600 transition-colors"
                        onClick={() => fileInputRef.current?.click()}
                      >
                        <Upload size={32} className="mb-2" />
                        <span className="text-sm text-center">
                          Clique ou arraste<br />para enviar
                        </span>
                      </div>
                    )}
                  </div>
                  
                  <input
                    ref={fileInputRef}
                    type="file"
                    accept="image/jpeg,image/png,image/webp,image/gif"
                    onChange={handleFileSelect}
                    className="hidden"
                    data-testid="logo-input"
                  />
                  
                  <div className="flex gap-2 mt-4">
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => fileInputRef.current?.click()}
                    >
                      <Upload size={16} className="mr-2" />
                      {currentLogo ? 'Trocar' : 'Enviar'}
                    </Button>
                  </div>
                </div>
                
                <div className="text-xs text-slate-500 text-center">
                  Formatos: JPG, PNG, WebP, GIF<br />
                  Tamanho máximo: 2MB
                </div>
              </CardContent>
            </Card>

            {/* Company Info Card */}
            <Card className="lg:col-span-2">
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Building2 size={20} />
                  Informações da Empresa
                </CardTitle>
                <CardDescription>
                  Dados básicos cadastrados no sistema
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <Label>Nome da Empresa</Label>
                    <Input value={companyData?.nome || ''} disabled className="bg-slate-50" />
                  </div>
                  <div>
                    <Label>CNPJ</Label>
                    <Input value={companyData?.cnpj || ''} disabled className="bg-slate-50" />
                  </div>
                  <div>
                    <Label>Email Principal</Label>
                    <Input value={companyData?.email || user?.email || ''} disabled className="bg-slate-50" />
                  </div>
                  <div>
                    <Label>Plano Atual</Label>
                    <div className="flex items-center gap-2 h-10">
                      <Badge className="bg-[#0d9488] text-white">
                        {company?.subscription_plan?.toUpperCase() || 'STARTER'}
                      </Badge>
                      <span className="text-sm text-slate-500">
                        {company?.subscription_status === 'trial' ? '(Período de teste)' : ''}
                      </span>
                    </div>
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>
        </TabsContent>

        {/* Financial Tab */}
        <TabsContent value="financial" className="mt-6">
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <FileText size={20} />
                Dados Financeiros para Faturamento
              </CardTitle>
              <CardDescription>
                Esses dados serão utilizados para emissão de notas fiscais e cobranças
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-6">
              {/* Razão Social / Nome Fantasia */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <Label htmlFor="razao_social">Razão Social *</Label>
                  <Input
                    id="razao_social"
                    value={financialData.razao_social}
                    onChange={(e) => setFinancialData(prev => ({ ...prev, razao_social: e.target.value }))}
                    placeholder="Razão Social da empresa"
                  />
                </div>
                <div>
                  <Label htmlFor="nome_fantasia">Nome Fantasia</Label>
                  <Input
                    id="nome_fantasia"
                    value={financialData.nome_fantasia}
                    onChange={(e) => setFinancialData(prev => ({ ...prev, nome_fantasia: e.target.value }))}
                    placeholder="Nome Fantasia"
                  />
                </div>
              </div>

              {/* CNPJ */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <Label htmlFor="cnpj_fin">CNPJ *</Label>
                  <Input
                    id="cnpj_fin"
                    value={financialData.cnpj}
                    onChange={(e) => handleCNPJChange(e.target.value)}
                    placeholder="00.000.000/0000-00"
                    className={cnpjError ? 'border-red-500' : ''}
                    maxLength={18}
                  />
                  {cnpjError && (
                    <p className="text-sm text-red-500 mt-1">{cnpjError}</p>
                  )}
                </div>
                <div>
                  <Label htmlFor="email_nf">E-mail para Nota Fiscal *</Label>
                  <Input
                    id="email_nf"
                    type="email"
                    value={financialData.email_nf}
                    onChange={(e) => setFinancialData(prev => ({ ...prev, email_nf: e.target.value }))}
                    placeholder="financeiro@empresa.com.br"
                  />
                </div>
              </div>

              {/* Inscrições */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <Label htmlFor="inscricao_estadual">Inscrição Estadual</Label>
                  <div className="flex items-center gap-4">
                    <Input
                      id="inscricao_estadual"
                      value={financialData.inscricao_estadual}
                      onChange={(e) => setFinancialData(prev => ({ ...prev, inscricao_estadual: e.target.value }))}
                      placeholder="Número da Inscrição Estadual"
                      disabled={financialData.inscricao_estadual_isento}
                      className={financialData.inscricao_estadual_isento ? 'bg-gray-100' : ''}
                    />
                    <div className="flex items-center gap-2 whitespace-nowrap">
                      <Checkbox
                        id="ie_isento"
                        checked={financialData.inscricao_estadual_isento}
                        onCheckedChange={(checked) => {
                          setFinancialData(prev => ({ 
                            ...prev, 
                            inscricao_estadual_isento: checked,
                            inscricao_estadual: checked ? '' : prev.inscricao_estadual
                          }));
                        }}
                      />
                      <Label htmlFor="ie_isento" className="text-sm cursor-pointer">Isento</Label>
                    </div>
                  </div>
                </div>
                <div>
                  <Label htmlFor="inscricao_municipal">Inscrição Municipal</Label>
                  <Input
                    id="inscricao_municipal"
                    value={financialData.inscricao_municipal}
                    onChange={(e) => setFinancialData(prev => ({ ...prev, inscricao_municipal: e.target.value }))}
                    placeholder="Número da Inscrição Municipal (CCM)"
                  />
                </div>
              </div>

              {/* Endereço */}
              <div className="border-t pt-6">
                <h4 className="font-medium text-[#1e3a5f] flex items-center gap-2 mb-4">
                  <MapPin className="h-4 w-4" />
                  Endereço de Faturamento
                </h4>
                
                <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
                  <div>
                    <Label htmlFor="cep">CEP *</Label>
                    <div className="relative">
                      <Input
                        id="cep"
                        value={financialData.endereco_cep}
                        onChange={(e) => handleCEPChange(e.target.value)}
                        placeholder="00000-000"
                        className={cepError ? 'border-red-500 pr-10' : 'pr-10'}
                        maxLength={9}
                      />
                      {loadingCEP ? (
                        <Loader2 className="absolute right-3 top-1/2 -translate-y-1/2 h-4 w-4 animate-spin text-[#0d9488]" />
                      ) : (
                        <Search className="absolute right-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
                      )}
                    </div>
                    {cepError && (
                      <p className="text-sm text-red-500 mt-1">{cepError}</p>
                    )}
                  </div>
                  <div className="md:col-span-2">
                    <Label htmlFor="logradouro">Logradouro *</Label>
                    <Input
                      id="logradouro"
                      value={financialData.endereco_logradouro}
                      onChange={(e) => setFinancialData(prev => ({ ...prev, endereco_logradouro: e.target.value }))}
                      placeholder="Rua, Avenida, etc."
                    />
                  </div>
                  <div>
                    <Label htmlFor="numero">Número *</Label>
                    <Input
                      id="numero"
                      value={financialData.endereco_numero}
                      onChange={(e) => setFinancialData(prev => ({ ...prev, endereco_numero: e.target.value }))}
                      placeholder="Nº"
                    />
                  </div>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mt-4">
                  <div>
                    <Label htmlFor="complemento">Complemento</Label>
                    <Input
                      id="complemento"
                      value={financialData.endereco_complemento}
                      onChange={(e) => setFinancialData(prev => ({ ...prev, endereco_complemento: e.target.value }))}
                      placeholder="Sala, Andar, etc."
                    />
                  </div>
                  <div>
                    <Label htmlFor="bairro">Bairro *</Label>
                    <Input
                      id="bairro"
                      value={financialData.endereco_bairro}
                      onChange={(e) => setFinancialData(prev => ({ ...prev, endereco_bairro: e.target.value }))}
                      placeholder="Bairro"
                    />
                  </div>
                  <div>
                    <Label htmlFor="cidade">Cidade *</Label>
                    <Input
                      id="cidade"
                      value={financialData.endereco_cidade}
                      onChange={(e) => setFinancialData(prev => ({ ...prev, endereco_cidade: e.target.value }))}
                      placeholder="Cidade"
                    />
                  </div>
                  <div>
                    <Label htmlFor="estado">Estado *</Label>
                    <select
                      id="estado"
                      value={financialData.endereco_estado}
                      onChange={(e) => setFinancialData(prev => ({ ...prev, endereco_estado: e.target.value }))}
                      className="flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2"
                    >
                      <option value="">Selecione</option>
                      {ESTADOS_BR.map(uf => (
                        <option key={uf} value={uf}>{uf}</option>
                      ))}
                    </select>
                  </div>
                </div>
              </div>

              <div className="flex justify-end pt-4 border-t">
                <Button 
                  onClick={handleSaveFinancialData}
                  disabled={savingFinancial}
                  className="bg-[#0d9488] hover:bg-[#0f766e]"
                >
                  {savingFinancial ? (
                    <>
                      <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                      Salvando...
                    </>
                  ) : (
                    <>
                      <Save className="h-4 w-4 mr-2" />
                      Salvar Dados Financeiros
                    </>
                  )}
                </Button>
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        {/* Settings Tab */}
        <TabsContent value="settings" className="mt-6 space-y-6">
          {/* Subscription Section */}
          <Card data-testid="subscription-section">
            <CardHeader>
              <CardTitle className="flex items-center gap-2 text-[#1e3a5f]">
                <Crown size={20} />
                Assinatura
              </CardTitle>
              <CardDescription>
                Gerencie sua assinatura e plano atual
              </CardDescription>
            </CardHeader>
            <CardContent>
              {loadingSubscription ? (
                <div className="flex items-center justify-center py-8">
                  <Loader2 className="h-6 w-6 animate-spin text-[#0d9488]" />
                </div>
              ) : subscriptionData ? (
                <div className="space-y-6">
                  {/* Plan Info */}
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                    <div className="bg-slate-50 rounded-lg p-4">
                      <div className="flex items-center gap-2 text-slate-600 text-sm mb-1">
                        <Crown size={16} />
                        Plano Atual
                      </div>
                      <div className="text-xl font-bold text-[#1e3a5f]">
                        {subscriptionData.plan_name || 'Starter'}
                      </div>
                      <div className="text-sm text-[#0d9488] font-medium">
                        R$ {subscriptionData.price_monthly?.toFixed(2) || '97,00'}/mês
                      </div>
                    </div>
                    
                    <div className="bg-slate-50 rounded-lg p-4">
                      <div className="flex items-center gap-2 text-slate-600 text-sm mb-1">
                        <CheckCircle2 size={16} />
                        Status
                      </div>
                      <div className="mt-1">
                        {getStatusBadge(subscriptionData.status)}
                      </div>
                      {subscriptionData.status === 'trial' && subscriptionData.trial_end_date && (
                        <div className="text-xs text-slate-500 mt-2">
                          Trial termina em: {formatDate(subscriptionData.trial_end_date)}
                        </div>
                      )}
                    </div>
                    
                    <div className="bg-slate-50 rounded-lg p-4">
                      <div className="flex items-center gap-2 text-slate-600 text-sm mb-1">
                        <Calendar size={16} />
                        {subscriptionData.status === 'cancelled' ? 'Acesso até' : 'Próxima Cobrança'}
                      </div>
                      <div className="text-lg font-semibold text-[#1e3a5f]">
                        {subscriptionData.status === 'cancelled' 
                          ? formatDate(subscriptionData.access_end_date) 
                          : formatDate(subscriptionData.next_due_date || subscriptionData.trial_end_date)
                        }
                      </div>
                    </div>
                  </div>
                  
                  {/* Cancelled Warning */}
                  {subscriptionData.status === 'cancelled' && (
                    <div className="bg-amber-50 border border-amber-200 rounded-lg p-4">
                      <div className="flex items-start gap-3">
                        <AlertTriangle className="h-5 w-5 text-amber-600 mt-0.5" />
                        <div>
                          <h4 className="font-medium text-amber-800">Assinatura Cancelada</h4>
                          <p className="text-sm text-amber-700 mt-1">
                            Você continuará tendo acesso até o dia <strong>{formatDate(subscriptionData.access_end_date)}</strong>. 
                            Após essa data, sua conta será bloqueada e o acesso aos dados será suspenso até uma nova assinatura.
                          </p>
                          <Button 
                            className="mt-3 bg-[#0d9488] hover:bg-[#0f766e]"
                            size="sm"
                            onClick={() => navigate('/planos')}
                          >
                            Reativar Assinatura
                          </Button>
                        </div>
                      </div>
                    </div>
                  )}
                  
                  {/* Features */}
                  {subscriptionData.features && subscriptionData.features.length > 0 && (
                    <div>
                      <h4 className="font-medium text-[#1e3a5f] mb-3">Recursos do Plano</h4>
                      <ul className="grid grid-cols-1 md:grid-cols-2 gap-2">
                        {subscriptionData.features.map((feature, idx) => (
                          <li key={idx} className="flex items-center gap-2 text-sm text-slate-600">
                            <CheckCircle2 size={14} className="text-[#0d9488]" />
                            {feature}
                          </li>
                        ))}
                      </ul>
                    </div>
                  )}
                  
                  {/* Cancel Button - Only show if not already cancelled */}
                  {subscriptionData.status !== 'cancelled' && subscriptionData.status !== 'expired' && (
                    <div className="border-t pt-4">
                      <Button 
                        variant="outline"
                        className="text-red-600 border-red-200 hover:bg-red-50 hover:border-red-300"
                        onClick={() => setShowCancelModal(true)}
                        data-testid="cancel-subscription-btn"
                      >
                        <Ban size={16} className="mr-2" />
                        Cancelar Assinatura
                      </Button>
                    </div>
                  )}
                </div>
              ) : (
                <div className="text-center py-8 text-slate-500">
                  <AlertCircle size={48} className="mx-auto mb-4 opacity-50" />
                  <p>Não foi possível carregar informações da assinatura</p>
                  <Button 
                    variant="outline" 
                    size="sm" 
                    className="mt-4"
                    onClick={fetchSubscriptionData}
                  >
                    <RefreshCw size={16} className="mr-2" />
                    Tentar Novamente
                  </Button>
                </div>
              )}
            </CardContent>
          </Card>

          {/* Danger Zone */}
          <Card className="border-red-200">
            <CardHeader>
              <CardTitle className="flex items-center gap-2 text-red-600">
                <AlertTriangle size={20} />
                Zona de Perigo
              </CardTitle>
              <CardDescription>
                Ações irreversíveis que afetam toda a conta
              </CardDescription>
            </CardHeader>
            <CardContent>
              <div className="bg-red-50 border border-red-200 rounded-lg p-4">
                <h4 className="font-medium text-red-800 mb-2">Excluir Conta</h4>
                <p className="text-sm text-red-700 mb-4">
                  Ao excluir sua conta, todos os dados da empresa serão permanentemente removidos, incluindo:
                </p>
                <ul className="list-disc list-inside text-sm text-red-700 mb-4 space-y-1">
                  <li>Todos os funcionários cadastrados</li>
                  <li>Avaliações e respostas</li>
                  <li>Treinamentos e certificados</li>
                  <li>Riscos identificados e planos de ação</li>
                  <li>Relatórios e documentos gerados</li>
                  <li>Histórico de pagamentos</li>
                </ul>
                <p className="text-sm font-medium text-red-800 mb-4">
                  Esta ação é IRREVERSÍVEL e não pode ser desfeita.
                </p>
                <Button 
                  variant="destructive"
                  onClick={() => setShowDeleteModal(true)}
                >
                  <Trash2 className="h-4 w-4 mr-2" />
                  Excluir Minha Conta
                </Button>
              </div>
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>

      {/* Upload Preview Modal */}
      <Dialog open={uploadModalOpen} onOpenChange={setUploadModalOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Confirmar Logo</DialogTitle>
            <DialogDescription>
              Verifique se a imagem está correta antes de salvar
            </DialogDescription>
          </DialogHeader>
          <div className="flex justify-center py-4">
            <div className="w-48 h-48 rounded-xl border-2 border-slate-200 overflow-hidden bg-white flex items-center justify-center">
              {previewUrl && (
                <img 
                  src={previewUrl} 
                  alt="Preview do logo" 
                  className="max-w-full max-h-full object-contain"
                />
              )}
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={handleCancelUpload}>
              <X size={16} className="mr-2" />
              Cancelar
            </Button>
            <Button 
              onClick={handleUploadLogo} 
              disabled={uploading}
              className="bg-[#0d9488] hover:bg-[#0f766e]"
            >
              {uploading ? (
                <>
                  <RefreshCw className="animate-spin mr-2" size={16} />
                  Enviando...
                </>
              ) : (
                <>
                  <CheckCircle2 size={16} className="mr-2" />
                  Confirmar
                </>
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Delete Account Modal */}
      <Dialog open={showDeleteModal} onOpenChange={setShowDeleteModal}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-red-600">
              <AlertTriangle size={20} />
              Confirmar Exclusão de Conta
            </DialogTitle>
            <DialogDescription>
              Esta ação é permanente e não pode ser desfeita.
            </DialogDescription>
          </DialogHeader>
          <div className="py-4 space-y-4">
            <p className="text-sm text-gray-600">
              Para confirmar a exclusão, digite <strong>"EXCLUIR MINHA CONTA"</strong> no campo abaixo:
            </p>
            <Input
              value={deleteConfirmText}
              onChange={(e) => setDeleteConfirmText(e.target.value)}
              placeholder="Digite aqui para confirmar"
              className="font-mono"
            />
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => {
              setShowDeleteModal(false);
              setDeleteConfirmText('');
            }}>
              Cancelar
            </Button>
            <Button 
              variant="destructive"
              onClick={handleDeleteAccount}
              disabled={deleting || deleteConfirmText !== 'EXCLUIR MINHA CONTA'}
            >
              {deleting ? (
                <>
                  <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                  Excluindo...
                </>
              ) : (
                <>
                  <Trash2 className="h-4 w-4 mr-2" />
                  Excluir Permanentemente
                </>
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Cancel Subscription Modal */}
      <Dialog open={showCancelModal} onOpenChange={setShowCancelModal}>
        <DialogContent data-testid="cancel-subscription-modal">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-amber-600">
              <AlertTriangle size={20} />
              Cancelar Assinatura
            </DialogTitle>
            <DialogDescription>
              Tem certeza que deseja cancelar sua assinatura?
            </DialogDescription>
          </DialogHeader>
          <div className="py-4 space-y-4">
            <div className="bg-amber-50 border border-amber-200 rounded-lg p-4">
              <div className="flex items-start gap-3">
                <Clock className="h-5 w-5 text-amber-600 mt-0.5 flex-shrink-0" />
                <div>
                  <p className="text-sm text-amber-800">
                    Você continuará tendo acesso até o dia <strong>{formatDate(subscriptionData?.access_end_date || subscriptionData?.trial_end_date || subscriptionData?.next_due_date)}</strong>.
                  </p>
                  <p className="text-sm text-amber-800 mt-2">
                    <strong>Após essa data, sua conta será bloqueada</strong> e o acesso aos dados será suspenso até uma nova assinatura.
                  </p>
                </div>
              </div>
            </div>
            
            <div className="text-sm text-slate-600">
              <p className="font-medium mb-2">O que acontece ao cancelar:</p>
              <ul className="space-y-1 ml-4">
                <li className="flex items-center gap-2">
                  <span className="w-1.5 h-1.5 bg-amber-500 rounded-full"></span>
                  Você terá acesso completo até o fim do período pago
                </li>
                <li className="flex items-center gap-2">
                  <span className="w-1.5 h-1.5 bg-amber-500 rounded-full"></span>
                  Não haverá novas cobranças
                </li>
                <li className="flex items-center gap-2">
                  <span className="w-1.5 h-1.5 bg-red-500 rounded-full"></span>
                  Após a data limite, sua conta será bloqueada
                </li>
                <li className="flex items-center gap-2">
                  <span className="w-1.5 h-1.5 bg-slate-400 rounded-full"></span>
                  Você poderá reativar a assinatura a qualquer momento
                </li>
              </ul>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowCancelModal(false)}>
              Manter Assinatura
            </Button>
            <Button 
              variant="destructive"
              onClick={handleCancelSubscription}
              disabled={cancelling}
              data-testid="confirm-cancel-btn"
            >
              {cancelling ? (
                <>
                  <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                  Cancelando...
                </>
              ) : (
                <>
                  <Ban className="h-4 w-4 mr-2" />
                  Confirmar Cancelamento
                </>
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default CompanyProfile;
